import axios from "axios";
import { traduceAccessError } from "@/composables/error.js";
import i18n from "@/i18n"; // Import the i18n instance
import { UAParser } from 'ua-parser-js';
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { fetchTokenQuery } from '@/queries/user.query';

// Initialize the agent at application startup.
const fpPromise = FingerprintJS.load();
const parser = new UAParser(navigator.userAgent);
const ua = parser.getResult().withClientHints();

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Yuzu-Key": import.meta.env.VITE_API_KEY,
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const fp = await fpPromise;
    const fingerPrintResult = await fp.get();
    const currentLocale = i18n.global.locale;

    let uaWithClientHints = await ua;
    delete uaWithClientHints.ua;

    const encodedBrowserInfo = btoa(String.fromCodePoint(...new TextEncoder().encode(JSON.stringify(uaWithClientHints))));

    config.headers["lang"] = currentLocale.value;
    config.headers["Accept-Language"] = currentLocale.value === 'fr' ? 'fr;q=0.9, en;q=0.8' : 'en;q=0.9, fr;q=0.8';
    config.headers["x-browser-fingerprint"] = fingerPrintResult.visitorId;
    config.headers["x-device-infos"] = encodedBrowserInfo;
    config.headers["x-app-name"] = 'frontend-orga';
    config.headers["x-app-environment"] = import.meta.env.VITE_ENV || 'dev';

    const tokenObject = await fetchTokenQuery();
    if (tokenObject) {
      config.headers["Authorization"] = `Bearer ${tokenObject.token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const useAxios = {
  async get(path, params, isBlob = false) {
    try {
      let config = {
        params
      }
      if (isBlob) {
        config.responseType = 'blob';
      }
      const response = await axiosInstance.get(`${path}`, config);

      return response;
    } catch (error) {
      if (error.response) traduceAccessError(error.response.data);

      throw error;
    }
  },
  async post(path, params, headers = {}) {
    try {
      const basicHeaders = {
        "Content-Type": "multipart/form-data",
      };

      const mergedHeaders = { ...basicHeaders, ...headers};
      const response = await axiosInstance.post(`${path}`, params, {
        headers: mergedHeaders,
      });

      return response;
    } catch (error) {
      if (error.response) traduceAccessError(error.response.data);

      throw error;
    }
  },
  async put(path, params, config = {}) {
    try {
      const response = await axiosInstance.put(`${path}`, params, config);

      return response;
    } catch (error) {
      if (error.response) traduceAccessError(error.response.data);

      throw error;
    }
  },
  async delete(path, params) {
    try {
      const response = await axiosInstance.delete(`${path}`, { data: params });

      return response;
    } catch (error) {
      if (error.response) traduceAccessError(error.response.data);

      throw error;
    }
  },
};

export { useAxios, axiosInstance };
